import React, { useState, useRef, createElement } from 'react';
import { Download, ExternalLink, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
type Format = 'svg' | 'png';
type MultiFormatAsset = {
  title: string;
  description: string;
  formats: {
    svg: string;
    png: string;
  };
};
type SingleFormatAsset = {
  title: string;
  description: string;
  imageUrl: string;
  fileExtension: string;
};
type BrandAssetWithSelectorProps = {
  asset: MultiFormatAsset;
};
type BrandAssetProps = {
  imageUrl: string;
  title: string;
  description: string;
  fileExtension?: string;
};
const BrandAssetWithSelector: React.FC<BrandAssetWithSelectorProps> = ({
  asset
}) => {
  const [selectedFormat, setSelectedFormat] = useState<Format>('svg');
  const [isDownloading, setIsDownloading] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageUrl = asset.formats[selectedFormat];
  const fileExtension = selectedFormat;
  const downloadWithCanvas = async () => {
    if (isDownloading) return;
    try {
      setIsDownloading(true);
      const toastId = toast.loading(`Downloading ${asset.title}...`);
      if (fileExtension === 'svg') {
        try {
          // Fetch SVG content and download directly
          const response = await fetch(imageUrl);
          if (!response.ok) throw new Error('Failed to fetch SVG');
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = asset.title.toLowerCase().replace(/\s+/g, '-') + '.svg';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          // Clean up the blob URL
          URL.revokeObjectURL(blobUrl);
          toast.success(`${asset.title} downloaded successfully`, {
            id: toastId,
            duration: 2000,
            style: {
              background: '#10b981',
              color: 'white',
              border: 'none'
            }
          });
        } catch (error) {
          console.error('SVG download failed:', error);
          // Fallback to opening in new tab
          window.open(imageUrl, '_blank');
          toast.info(<div data-id="element-8">
              <p data-id="element-9">SVG opened in new tab</p>
              <p className="text-xs mt-1" data-id="element-10">
                Right-click and select "Save Image As" to download
              </p>
            </div>, {
            id: toastId,
            duration: 5000,
            style: {
              background: '#f59e0b',
              color: 'white',
              border: 'none'
            }
          });
        }
        setIsDownloading(false);
        return;
      }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = canvasRef.current;
          if (!canvas) throw new Error('Canvas element not available');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Could not get canvas context');
          ctx.drawImage(img, 0, 0);
          let dataUrl;
          try {
            dataUrl = canvas.toDataURL('image/png');
          } catch (e) {
            dataUrl = canvas.toDataURL('image/png');
          }
          const link = document.createElement('a');
          link.href = dataUrl;
          link.download = asset.title.toLowerCase().replace(/\s+/g, '-') + '.' + fileExtension;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success(`${asset.title} downloaded successfully`, {
            id: toastId,
            duration: 2000,
            style: {
              background: '#10b981',
              color: 'white',
              border: 'none'
            }
          });
        } catch (error) {
          console.error('Canvas processing failed:', error);
          fallbackToNewTab(toastId);
        }
        setIsDownloading(false);
      };
      img.onerror = () => {
        console.error('Image failed to load for canvas processing');
        fallbackToNewTab(toastId);
        setIsDownloading(false);
      };
      const cacheBustUrl = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}cb=${Date.now()}`;
      img.src = cacheBustUrl;
    } catch (error) {
      console.error('Download failed completely:', error);
      toast.error(`Failed to download ${asset.title}`, {
        duration: 3000,
        style: {
          background: '#ef4444',
          color: 'white',
          border: 'none'
        }
      });
      setIsDownloading(false);
    }
  };
  const fallbackToNewTab = (toastId?: string) => {
    window.open(imageUrl, '_blank');
    const toastMessage = <div data-id="element-11">
        <p data-id="element-12">Image opened in new tab (direct download not possible)</p>
        <p className="text-xs mt-1" data-id="element-13">
          Right-click and select "Save Image As" to download
        </p>
      </div>;
    if (toastId) {
      toast.success(toastMessage, {
        id: toastId,
        duration: 5000,
        style: {
          background: '#f59e0b',
          color: 'white',
          border: 'none'
        }
      });
    } else {
      toast.success(toastMessage, {
        duration: 5000,
        style: {
          background: '#f59e0b',
          color: 'white',
          border: 'none'
        }
      });
    }
  };
  const handleOpenInNewTab = () => {
    window.open(imageUrl, '_blank');
    toast.success(<div data-id="element-14">
        <p data-id="element-15">Image opened in new tab</p>
        <p className="text-xs mt-1" data-id="element-16">
          Right-click and select "Save Image As" to download
        </p>
      </div>, {
      duration: 5000,
      style: {
        background: '#10b981',
        color: 'white',
        border: 'none'
      }
    });
  };
  return <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-17">
      <canvas ref={canvasRef} style={{
      display: 'none'
    }} data-id="element-18" />
      <div className="h-48 bg-slate-100 flex items-center justify-center p-4" data-id="element-19">
        <img src={imageUrl} alt={asset.title} className="max-h-full max-w-full object-contain" data-id="element-20" />
      </div>
      <div className="p-4" data-id="element-21">
        <h3 className="font-medium text-slate-900" data-id="element-22">{asset.title}</h3>
        <p className="text-sm text-slate-500 mt-1" data-id="element-23">{asset.description}</p>
        <div className="flex items-center gap-2 mt-2" data-id="element-24">
          <span className="text-xs text-slate-500" data-id="element-25">Format:</span>
          <div className="relative" data-id="element-26">
            <select value={selectedFormat} onChange={e => setSelectedFormat(e.target.value as Format)} className="appearance-none text-xs font-mono bg-slate-100 text-slate-700 pl-2 pr-6 py-1 rounded border-0 cursor-pointer hover:bg-slate-200 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-300" data-id="element-27">
              <option value="svg" data-id="element-28">.svg</option>
              <option value="png" data-id="element-29">.png</option>
            </select>
            <ChevronDown size={12} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" data-id="element-30" />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mt-3" data-id="element-31">
          <button onClick={downloadWithCanvas} disabled={isDownloading} className={`flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${isDownloading ? 'bg-slate-400 text-white cursor-wait' : 'bg-[#001617] text-white hover:bg-[#001617]/90'}`} data-id="element-32">
            {isDownloading ? <>
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-33"></div>
                Downloading...
              </> : <>
                <Download size={16} data-id="element-34" />
                Download
              </>}
          </button>
          <button onClick={handleOpenInNewTab} className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-35">
            <ExternalLink size={16} data-id="element-36" />
            Open in New Tab
          </button>
        </div>
      </div>
    </div>;
};
const BrandAsset: React.FC<BrandAssetProps> = ({
  imageUrl,
  title,
  description,
  fileExtension = 'png'
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const downloadWithCanvas = async () => {
    if (isDownloading) return;
    try {
      setIsDownloading(true);
      const toastId = toast.loading(`Downloading ${title}...`);
      if (fileExtension === 'svg') {
        window.open(imageUrl, '_blank');
        toast.success(<div data-id="element-37">
            <p data-id="element-38">SVG opened in new tab (SVGs require manual saving)</p>
            <p className="text-xs mt-1" data-id="element-39">
              Right-click and select "Save Image As" to download
            </p>
          </div>, {
          id: toastId,
          duration: 5000
        });
        setIsDownloading(false);
        return;
      }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = canvasRef.current;
          if (!canvas) throw new Error('Canvas element not available');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Could not get canvas context');
          ctx.drawImage(img, 0, 0);
          let dataUrl;
          try {
            dataUrl = canvas.toDataURL(`image/${fileExtension === 'jpg' ? 'jpeg' : fileExtension}`);
          } catch (e) {
            dataUrl = canvas.toDataURL('image/png');
          }
          const link = document.createElement('a');
          link.href = dataUrl;
          link.download = title.toLowerCase().replace(/\s+/g, '-') + '.' + fileExtension;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success(`${title} downloaded successfully`, {
            id: toastId,
            duration: 2000
          });
        } catch (error) {
          console.error('Canvas processing failed:', error);
          fallbackToNewTab(toastId);
        }
        setIsDownloading(false);
      };
      img.onerror = () => {
        console.error('Image failed to load for canvas processing');
        fallbackToNewTab(toastId);
        setIsDownloading(false);
      };
      const cacheBustUrl = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}cb=${Date.now()}`;
      img.src = cacheBustUrl;
    } catch (error) {
      console.error('Download failed completely:', error);
      toast.error(`Failed to download ${title}`, {
        duration: 3000
      });
      setIsDownloading(false);
    }
  };
  const fallbackToNewTab = (toastId?: string) => {
    window.open(imageUrl, '_blank');
    const toastMessage = <div data-id="element-40">
        <p data-id="element-41">Image opened in new tab (direct download not possible)</p>
        <p className="text-xs mt-1" data-id="element-42">
          Right-click and select "Save Image As" to download
        </p>
      </div>;
    if (toastId) {
      toast.success(toastMessage, {
        id: toastId,
        duration: 5000,
        style: {
          background: '#f59e0b',
          color: 'white',
          border: 'none'
        }
      });
    } else {
      toast.success(toastMessage, {
        duration: 5000,
        style: {
          background: '#f59e0b',
          color: 'white',
          border: 'none'
        }
      });
    }
  };
  const handleOpenInNewTab = () => {
    window.open(imageUrl, '_blank');
    toast.success(<div data-id="element-43">
        <p data-id="element-44">Image opened in new tab</p>
        <p className="text-xs mt-1" data-id="element-45">
          Right-click and select "Save Image As" to download
        </p>
      </div>, {
      duration: 5000,
      style: {
        background: '#10b981',
        color: 'white',
        border: 'none'
      }
    });
  };
  return <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-46">
      <canvas ref={canvasRef} style={{
      display: 'none'
    }} data-id="element-47" />
      <div className="h-48 bg-slate-100 flex items-center justify-center p-4" data-id="element-48">
        <img src={imageUrl} alt={title} className="max-h-full max-w-full object-contain" data-id="element-49" />
      </div>
      <div className="p-4" data-id="element-50">
        <h3 className="font-medium text-slate-900" data-id="element-51">{title}</h3>
        <p className="text-sm text-slate-500 mt-1" data-id="element-52">{description}</p>
        <div className="flex items-center mt-2" data-id="element-53">
          <span className="text-xs font-mono bg-slate-100 text-slate-600 px-2 py-1 rounded" data-id="element-54">
            .{fileExtension}
          </span>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mt-3" data-id="element-55">
          <button onClick={downloadWithCanvas} disabled={isDownloading} className={`flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${isDownloading ? 'bg-slate-400 text-white cursor-wait' : 'bg-[#001617] text-white hover:bg-[#001617]/90'}`} data-id="element-56">
            {isDownloading ? <>
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-57"></div>
                Downloading...
              </> : <>
                <Download size={16} data-id="element-58" />
                Download
              </>}
          </button>
          <button onClick={handleOpenInNewTab} className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-59">
            <ExternalLink size={16} data-id="element-60" />
            Open in New Tab
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-2" data-id="element-61">
          {fileExtension === 'svg' ? 'SVG files may require manual saving through the browser.' : ''}
        </p>
      </div>
    </div>;
};
const BrandAssets: React.FC = () => {
  // Assets with both SVG and PNG formats (consolidated with selector)
  const multiFormatAssets: MultiFormatAsset[] = [{
    title: 'Brandmark (Circular)',
    description: 'Circular version of the brandmark',
    formats: {
      svg: 'https://cdn.magicpatterns.com/uploads/9EKqqVpcZZrfL1Lbu8kpqE/brandmark.svg',
      png: 'https://cdn.magicpatterns.com/uploads/6UynxNi6YAbkWUtiYvs9q3/brandmark.png'
    }
  }, {
    title: 'Brandmark (Dark)',
    description: 'Dark version of the brandmark for light backgrounds',
    formats: {
      svg: 'https://cdn.magicpatterns.com/uploads/bycsd1Q3LW5qANTYJ9vj8N/brandmark-dark.svg',
      png: 'https://cdn.magicpatterns.com/uploads/fUbtzAWm37A1WqKcSXiJPv/brandmark-dark.png'
    }
  }, {
    title: 'Brandmark (Gradient)',
    description: 'Gradient version of the brandmark for marketing materials',
    formats: {
      svg: 'https://cdn.magicpatterns.com/uploads/kvWVabFBAGnadvCjYZBQBQ/brandmark-gradient.svg',
      png: 'https://cdn.magicpatterns.com/uploads/utT427pNwFuaYNJb3QPGv3/brandmark-gradient.png'
    }
  }, {
    title: 'Brandmark (Light)',
    description: 'Light version of the brandmark for dark backgrounds',
    formats: {
      svg: 'https://cdn.magicpatterns.com/uploads/3L3m4hU26cxEDwq4HVssKJ/brandmark-light.svg',
      png: 'https://cdn.magicpatterns.com/uploads/mmsq5aYF2txkZxgAyU4TQa/brandmark-light.png'
    }
  }, {
    title: 'Logo (Dark)',
    description: 'Full logo with text in dark version',
    formats: {
      svg: 'https://cdn.magicpatterns.com/uploads/b38zJiCutuPnNnhrn76EtR/logo-dark.svg',
      png: 'https://cdn.magicpatterns.com/uploads/rs9qko3zW5oo7xusyyS64F/logo-dark.png'
    }
  }, {
    title: 'Logo (Gradient)',
    description: 'Full logo with gradient brandmark',
    formats: {
      svg: 'https://cdn.magicpatterns.com/uploads/4dLojDAyN6xnxS7vQvEjtm/logo-gradient-dark.svg',
      png: 'https://cdn.magicpatterns.com/uploads/qcbzuL2uSFvgdkx61PWqEY/logo-gradient-dark.png'
    }
  }, {
    title: 'Logo (Light)',
    description: 'Full logo with text in light version for dark backgrounds',
    formats: {
      svg: 'https://cdn.magicpatterns.com/uploads/8cQ62yV2hrGbB93HsJArDL/logo-light.svg',
      png: 'https://cdn.magicpatterns.com/uploads/eR4uvysVae6augF7jxTP4H/logo-light.png'
    }
  }, {
    title: 'Logo (Gradient Light)',
    description: 'Full logo with gradient brandmark for dark backgrounds',
    formats: {
      svg: 'https://cdn.magicpatterns.com/uploads/pn7MNCd9uxb5ekjWu56dxB/logo-gradient-light.svg',
      png: 'https://cdn.magicpatterns.com/uploads/ecA7ZZtsmVYkqkUyvWiaT4/logo-gradient-light.png'
    }
  }, {
    title: 'Avatar 01',
    description: 'First avatar variant for user profiles',
    formats: {
      svg: 'https://cdn.magicpatterns.com/uploads/sJULuxPxaLoFTVn6XM5avN/avatar-01.svg',
      png: 'https://cdn.magicpatterns.com/uploads/5fSUrj8MJiYU7SJt3k2tWy/avatar-01.png'
    }
  }, {
    title: 'Avatar 02',
    description: 'Second avatar variant for user profiles',
    formats: {
      svg: 'https://cdn.magicpatterns.com/uploads/wFTkLejhzYFpjWfEVzFguy/avatar-02.svg',
      png: 'https://cdn.magicpatterns.com/uploads/nYoE8tE9jnTjte3s17RijX/avatar-02.png'
    }
  }];
  return <div data-id="element-62">
      <h2 className="text-xl font-semibold text-slate-800 mb-3" data-id="element-63">
        Brand Assets
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-64">
        {multiFormatAssets.map((asset, index) => <BrandAssetWithSelector key={index} asset={asset} data-id="element-65" />)}
      </div>
    </div>;
};
export default BrandAssets;